import React, { useEffect, useState } from "react";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Form,
  Input,
  Button,
  message,
  Switch,
  InputNumber,
  Image,
  Tag,
  Popconfirm,
} from "antd";
import ButtonWrapper from "../../../components/reuseComp/ButtonWrapper";
import UploadFoodImage from "../../../../lib/UploadFoodImage";
import { removeSpecialChars } from "../../../../lib/utils";
import CommonListCard from "../../../../views/components/reuseComp/CommonListCard";
import {
  useIndustryDelete,
  useIndustryList,
  useIndustrySave,
  useIndustryDetail,
} from "../../../../hooks/api/industry/hook";
import { IndustryDetail, IndustryItem } from "../../../../types/api/industry";
import TextArea from "antd/es/input/TextArea";

const IndustryList = ({ allowURLStateUpdate = true }) => {
  const [messageApi, contextHolder] = message.useMessage();

  const DEFAULT_NUQS_CONFIG = {
    throttleMs: !allowURLStateUpdate ? Infinity : 0,
  };

  const { data, refetchWithQuery: refetch } = useIndustryList({
    page: 1,
    limit: 20,
    ...DEFAULT_NUQS_CONFIG,
  });

  const industryList: IndustryItem[] = data?.data ?? [];
  const qData = data?.qData ?? { page: 1, limit: 20 };

  const { mutate: saveIndustry, isPending } = useIndustrySave({ qData });
  const { mutate: deleteIndustry } = useIndustryDelete({ qData });

  const [selectedSlug, setSelectedSlug] = useState<string>("");

  const { data: industryDetail } = useIndustryDetail(selectedSlug);

  const [editingIndustry, setEditingIndustry] = useState<IndustryDetail | null>(
    null,
  );
  const [Editing, setEditing] = useState(false);
  const [form] = Form.useForm();

  const watchHeading = Form.useWatch(["banner", "heading"], form);

  useEffect(() => {
    if (industryDetail?.data) {
      setEditingIndustry(industryDetail.data);
    }
  }, [industryDetail]);

  useEffect(() => {
    if (editingIndustry) {
      form.setFieldsValue({
        banner: { 
          heading: editingIndustry.banner?.heading 
        },
        slug: editingIndustry.slug,
        image: editingIndustry.image,
        metaDescription: editingIndustry.metaDescription,
        order: editingIndustry.order,
        status: editingIndustry.status,
      });
    }
  }, [editingIndustry, form]);

  useEffect(() => {
    if (!editingIndustry?._id) {
      form.setFieldValue("slug", removeSpecialChars(watchHeading));
    }
  }, [watchHeading, editingIndustry, form]);

  const handleCancel = () => {
    setEditingIndustry(null);
    setEditing(false);
    form.resetFields();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const payload: IndustryDetail = {
        _id: editingIndustry?._id,
        slug: values.slug,
        banner: {
          heading: values.banner?.heading,
        },
        metaDescription: values.metaDescription,
        image: values.image,
        order: values.order ?? 0,
        status: values.status ?? false,
        isDeleted: false,
      };

      saveIndustry(payload, {
        onSuccess: () => {
          messageApi.success("Industry saved successfully");
          refetch({ page: qData.page, limit: qData.limit, _ts: Date.now() });
          handleCancel();
        },
        onError: (error) => {
          messageApi.error("Failed to save industry");
          console.error(error);
        },
      });
    } catch (err) {
      console.log("Validation failed:", err);
    }
  };

  const handleDelete = (id: string) => {
    deleteIndustry(
      { id },
      {
        onSuccess: () => {
          messageApi.success("Industry deleted successfully");
          refetch({ page: qData.page, limit: qData.limit });
        },
        onError: (error) => {
          messageApi.error("Failed to delete industry");
          console.error(error);
        },
      },
    );
  };

  return (
    <div>
      {contextHolder}

      {Editing ? (
        <div>
          <div className="bg-[#F9F9F9] p-4 sm:p-6 rounded-2xl shadow-sm mb-6">
            <Form
              layout="vertical"
              form={form}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <Form.Item
                label="Heading"
                name={["banner", "heading"]}
                rules={[{ required: true, message: "Heading is required" }]}
              >
                <Input placeholder="enter heading..." />
              </Form.Item>

              
              <Form.Item
                label="Meta Description"
                name="metaDescription"
                rules={[{ required: true, message: "Meta Description is required" }]}
              >
                <TextArea placeholder="enter meta description..." />
              </Form.Item>

              <Form.Item
                label="Slug"
                name="slug"
                rules={[{ required: true, message: "Slug is required" }]}
              >
                <Input disabled={!!editingIndustry?._id} />
              </Form.Item>

              <Form.Item label="Status" name="status" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>

              <Form.Item label="Order" name="order">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Image"
                name="image"
                rules={[{ required: true, message: "Image is required" }]}
                className="sm:col-span-2"
              >
                <UploadFoodImage maxUploadCount={1} />
              </Form.Item>
            </Form>
          </div>
          <div className="flex justify-start gap-3 mt-7 sticky bottom-5">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={isPending}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper onClick={handleCancel}>Cancel</ButtonWrapper>
          </div>
        </div>
      ) : (
        <div className="rounded-[20px] overflow-hidden">
          <div className="hidden sm:block rounded-[20px] overflow-hidden">
            <table className="hidden sm:table min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Image</th>
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Order</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {industryList?.map((item) => (
                  <tr
                    key={item._id}
                    className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]"
                  >
                    <td className="py-3 px-4 ">
                      <Image
                        src={item.image}
                        alt={item.heading || "Industry"}
                        width={60}
                        height={60}
                        fallback="/placeholder.jpg"
                        className="rounded-md object-cover"
                      />
                    </td>
                    <td className="py-3 px-4 ">{item.heading}</td>
                    <td className="py-3 px-4 ">{item.order}</td>
                    <td className="py-3 px-4 ">
                      <Tag color={item.status ? "green" : "red"}>
                        {item.status ? "Active" : "Inactive"}
                      </Tag>
                    </td>
                    <td className="py-3 px-4 text-primary">
                      <div className="flex justify-center items-center gap-4 text-lg">
                        <EditOutlined
                          onClick={() => {
                            setSelectedSlug(item.slug);
                            setEditing(true);
                          }}
                          className="cursor-pointer text-primary"
                        />
                        <Popconfirm
                          title="Are you sure to delete this industry?"
                          onConfirm={() => handleDelete(item._id!)}
                        >
                          <DeleteOutlined className="cursor-pointer text-red-500" />
                        </Popconfirm>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="bg-[#F9F9F9] shadow-sm p-2 sm:p-3">
              <div className="flex justify-end items-center">
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => setEditing(true)}
                  type="dashed"
                  className="text-primary font-semibold"
                >
                  Add Industry
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="sm:hidden space-y-4">
        {!Editing && (
          <>
        {industryList?.map((item) => (
          <CommonListCard
            service={item}
            form={form}
            setEditingService={setEditingIndustry}
            setEditing={setEditing}
            handleDelete={handleDelete}
          />
        ))}
        </>
        )}
      </div>
    </div>
  );
};

export default IndustryList;
